// New tagged slice for the migrated `_features/billingUnits` feature -
// consolidates TWO duplicate injectEndpoints calls that both existed on
// the same apiSlice for billing-unit routes with overlapping endpoint
// names (the old `features/api/billingUnits.js`, since removed entirely,
// and features/billingUnitsFeature/api/billingUnitsApi.js).
//
// Fixes a live, currently-shipping bug in the process: RTK Query's
// injectEndpoints silently no-ops a second same-named endpoint injection
// (overrideExisting: false by default), and the lean file's
// getAllBillingUnits (GET /billingUnits, camelCase plural - never a real
// backend route) was winning the collision over the fat file's correct
// getAllBillingUnits (GET /billingunit/, per apiRoutes.billingUnit.get).
// Confirmed live: the billing units list 404'd on every page load and the
// empty state silently swallowed it. The lean file's changeBillingUnitStatus
// (PUT /billingUnits/changeStatus) was also wired directly into
// BillingUnitsPage and was equally dead. Both are fixed here by writing
// one correct set of URLs/methods, cross-checked against
// simplifyserver/routes/billingUnit.js (mounted at /api/billingunit).
//
// Drops deleteBillingUnits (apiRoutes.billingUnit.delete is a real backend
// route, just zero UI consumers - archive/activate via changeStatus is
// what the page actually uses, same as every other migrated feature this
// session) and the lean file's unused singular create/edit hooks
// (useCreateBillingUnitMutation/useEditBillingUnitMutation - confirmed zero
// consumers, the real modal always used the fat plural hooks).
import { apiRoutes } from "../../../_config/apiRoutes";
import { apiSlice } from "../../../_api/apiSlice";

export const billingUnitApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllBillingUnits: builder.query({
      query: (params) => ({
        url: apiRoutes.billingUnit.get.path,
        method: apiRoutes.billingUnit.get.reqType,
        params,
      }),
      providesTags: ["BillingUnits"],
    }),
    getBillingUnits: builder.query({
      query: (params) => ({
        url: apiRoutes.billingUnit.getAll.path,
        method: apiRoutes.billingUnit.getAll.reqType,
        params,
      }),
      providesTags: ["BillingUnits"],
    }),
    createBillingUnits: builder.mutation({
      query: (data) => ({
        url: apiRoutes.billingUnit.create.path,
        method: apiRoutes.billingUnit.create.reqType,
        body: data,
      }),
      invalidatesTags: ["BillingUnits"],
    }),
    editBillingUnits: builder.mutation({
      query: (data) => ({
        url: apiRoutes.billingUnit.edit.path,
        method: apiRoutes.billingUnit.edit.reqType,
        body: data,
      }),
      invalidatesTags: ["BillingUnits"],
    }),
    changeBillingUnitStatus: builder.mutation({
      query: (data) => ({
        url: "/billingunit/changeStatus",
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: ["BillingUnits"],
    }),
  }),
});

export const {
  useGetAllBillingUnitsQuery,
  useGetBillingUnitsQuery,
  useCreateBillingUnitsMutation,
  useEditBillingUnitsMutation,
  useChangeBillingUnitStatusMutation,
} = billingUnitApi;
